import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config';

// ── Column definitions ────────────────────────────────────────────────────────
const ALL_COLUMNS = [
  { key: 'id',              label: 'ID',              defaultVisible: true  },
  { key: 'name',            label: 'Name',            defaultVisible: true  },
  { key: 'assetType',       label: 'Type',            defaultVisible: true  },
  { key: 'classification',  label: 'Classification',  defaultVisible: false },
  { key: 'status',          label: 'Status',          defaultVisible: true  },
  { key: 'criticality',     label: 'Criticality',     defaultVisible: true  },
  { key: 'serialNumber',    label: 'Serial No.',      defaultVisible: true  },
  { key: 'manufacturer',    label: 'Manufacturer',    defaultVisible: false },
  { key: 'model',           label: 'Model',           defaultVisible: false },
  { key: 'department',      label: 'Department',      defaultVisible: true  },
  { key: 'assignedUser',    label: 'Assigned To',     defaultVisible: true  },
  { key: 'owner',           label: 'Owner',           defaultVisible: false },
  { key: 'site',            label: 'Site/Client',     defaultVisible: false },
  { key: 'building',        label: 'Building',        defaultVisible: false },
  { key: 'floor',           label: 'Floor',           defaultVisible: false },
  { key: 'room',            label: 'Room',            defaultVisible: false },
  { key: 'rack',            label: 'Rack',            defaultVisible: false },
  { key: 'invoiceNumber',   label: 'Invoice #',       defaultVisible: false },
  { key: 'budgetNumber',    label: 'Budget #',        defaultVisible: false },
  { key: 'purchaseDate',    label: 'Purchase Date',   defaultVisible: false },
  { key: 'warrantyExpiration', label: 'Warranty Exp', defaultVisible: true  },
  { key: 'endOfLifeDate',   label: 'End of Life',     defaultVisible: false },
  { key: 'depreciationValue', label: 'Depreciation',  defaultVisible: false },
  { key: 'supplier',        label: 'Supplier',        defaultVisible: false },
  { key: 'lastAuditDate',   label: 'Last Audit',      defaultVisible: false },
  { key: 'notes',           label: 'Notes',           defaultVisible: false },
  { key: 'createdAt',       label: 'Created',         defaultVisible: false },
  { key: 'actions',         label: 'Actions',         defaultVisible: true  },
];

const STATUS_OPTIONS   = ['ACTIVE', 'INACTIVE', 'DECOMMISSIONED', 'IN_STORAGE'];
const CRIT_OPTIONS     = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const statusColor = { ACTIVE: '#28a745', INACTIVE: '#ffc107', DECOMMISSIONED: '#dc3545', IN_STORAGE: '#6c757d' };
const critColor   = { LOW: '#6c757d', MEDIUM: '#17a2b8', HIGH: '#ffc107', CRITICAL: '#dc3545' };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const token   = () => localStorage.getItem('token');

// ── Blank form state ──────────────────────────────────────────────────────────
const blankForm = () => ({
  name: '', description: '', assetType: '', classification: '', serialNumber: '',
  manufacturer: '', model: '', status: 'ACTIVE', criticality: '',
  building: '', floor: '', room: '', rack: '',
  purchaseDate: '', supplier: '', invoiceNumber: '', budgetNumber: '',
  warrantyExpiration: '', endOfLifeDate: '', depreciationValue: '',
  configSpecs: '', notes: '', relatedTickets: '', lastAuditDate: '',
  departmentId: '', ownerId: '', assignedUserId: '', siteId: '',
  equipmentAssignmentId: '',
});

export default function AssetManagement() {
  const navigate = useNavigate();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [assets,    setAssets]    = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [binView,   setBinView]   = useState(false);
  const [meta,      setMeta]      = useState({ departments: [], sites: [], users: [] });

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCrit,   setFilterCrit]   = useState('');
  const [filterDept,   setFilterDept]   = useState('');
  const LIMIT = 50;

  // ── Column visibility ────────────────────────────────────────────────────────
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem('assetMgmt_visibleCols');
      if (saved) return new Set(JSON.parse(saved));
    } catch (_) {}
    return new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  // ── Modals ───────────────────────────────────────────────────────────────────
  const [showForm,    setShowForm]    = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [form,        setForm]        = useState(blankForm);
  const [formError,   setFormError]   = useState('');
  const [formSaving,  setFormSaving]  = useState(false);

  const [historyAsset,  setHistoryAsset]  = useState(null);
  const [history,       setHistory]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile,       setBulkFile]       = useState(null);
  const [bulkResult,     setBulkResult]     = useState(null);
  const [bulkLoading,    setBulkLoading]    = useState(false);

  const [toast, setToast] = useState(null);

  // ── Fetch assets ─────────────────────────────────────────────────────────────
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      if (binView) {
        const r = await fetch(`${API_BASE}/admin/assets/bin`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const d = await r.json();
        setAssets(d.assets || []);
        setTotal(d.assets?.length || 0);
      } else {
        const params = new URLSearchParams({ page, limit: LIMIT });
        if (search)       params.set('search',       search);
        if (filterType)   params.set('type',         filterType);
        if (filterStatus) params.set('status',       filterStatus);
        if (filterCrit)   params.set('criticality',  filterCrit);
        if (filterDept)   params.set('departmentId', filterDept);
        const r = await fetch(`${API_BASE}/admin/assets?${params}`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const d = await r.json();
        setAssets(d.assets || []);
        setTotal(d.total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [binView, page, search, filterType, filterStatus, filterCrit, filterDept]);

  const fetchMeta = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/admin/assets/meta`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await r.json();
      setMeta(d);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // Close col picker when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Toast ────────────────────────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── CSV Export ───────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (search)       params.set('search',      search);
    if (filterType)   params.set('type',        filterType);
    if (filterStatus) params.set('status',      filterStatus);
    if (filterCrit)   params.set('criticality', filterCrit);
    if (filterDept)   params.set('departmentId', filterDept);
    const url = `${API_BASE}/admin/assets/export/csv?${params}`;
    const a = document.createElement('a');
    a.href = url;
    const authToken = token();
    // Use fetch to download with auth header
    fetch(url, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `assets_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
      });
  };

  // ── CSV Template Download ────────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    const headers = 'name,assetType,classification,status,criticality,serialNumber,manufacturer,model,description,departmentId,departmentName,supplier,invoiceNumber,budgetNumber,depreciationValue,building,floor,room,rack,purchaseDate,warrantyExpiration,endOfLifeDate,lastAuditDate,notes,relatedTickets,configSpecs';
    const example = 'My Server,Server,Confidential,ACTIVE,HIGH,SN123456,Dell,PowerEdge R750,Primary web server,1,,Dell Technologies,INV-001,BUD-2026-01,5000.00,Building A,2,Room 202,Rack 3A,2026-01-15,2028-01-15,2031-01-15,,Replace SSD in Q3,,CPU:2xIntel Xeon';
    const blob = new Blob([headers + '\n' + example], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'assets_template.csv';
    link.click();
  };

  // ── Bulk upload ───────────────────────────────────────────────────────────────
  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkLoading(true);
    setBulkResult(null);
    const fd = new FormData();
    fd.append('file', bulkFile);
    try {
      const r = await fetch(`${API_BASE}/admin/assets/bulk-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      const d = await r.json();
      setBulkResult(d);
      if (d.created > 0) fetchAssets();
    } catch (e) {
      setBulkResult({ error: e.message });
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Soft delete / hard delete / restore ──────────────────────────────────────
  const handleSoftDelete = async (id) => {
    if (!window.confirm('Move this asset to recycle bin?')) return;
    const r = await fetch(`${API_BASE}/admin/assets/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
    });
    if (r.ok) { showToast('Asset moved to recycle bin'); fetchAssets(); }
    else { const d = await r.json(); showToast(d.message, 'error'); }
  };

  const handleRestore = async (id) => {
    const r = await fetch(`${API_BASE}/admin/assets/bin/${id}/restore`, {
      method: 'POST', headers: { Authorization: `Bearer ${token()}` },
    });
    if (r.ok) { showToast('Asset restored'); fetchAssets(); }
    else { const d = await r.json(); showToast(d.message, 'error'); }
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('Permanently delete this asset? This cannot be undone.')) return;
    const r = await fetch(`${API_BASE}/admin/assets/bin/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
    });
    if (r.ok) { showToast('Asset permanently deleted'); fetchAssets(); }
    else { const d = await r.json(); showToast(d.message, 'error'); }
  };

  // ── History modal ─────────────────────────────────────────────────────────────
  const openHistory = async (asset) => {
    setHistoryAsset(asset);
    setHistoryLoading(true);
    setHistory([]);
    try {
      const r = await fetch(`${API_BASE}/admin/assets/${asset.id}/history`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await r.json();
      setHistory(d.history || []);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  // ── Create / Edit form ────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (asset) => {
    setEditingId(asset.id);
    setForm({
      name:              asset.name || '',
      description:       asset.description || '',
      assetType:         asset.assetType || '',
      classification:    asset.classification || '',
      serialNumber:      asset.serialNumber || '',
      manufacturer:      asset.manufacturer || '',
      model:             asset.model || '',
      status:            asset.status || 'ACTIVE',
      criticality:       asset.criticality || '',
      building:          asset.building || '',
      floor:             asset.floor || '',
      room:              asset.room || '',
      rack:              asset.rack || '',
      purchaseDate:      asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
      supplier:          asset.supplier || '',
      invoiceNumber:     asset.invoiceNumber || '',
      budgetNumber:      asset.budgetNumber || '',
      warrantyExpiration: asset.warrantyExpiration ? asset.warrantyExpiration.split('T')[0] : '',
      endOfLifeDate:     asset.endOfLifeDate ? asset.endOfLifeDate.split('T')[0] : '',
      depreciationValue: asset.depreciationValue != null ? String(asset.depreciationValue) : '',
      configSpecs:       asset.configSpecs || '',
      notes:             asset.notes || '',
      relatedTickets:    asset.relatedTickets || '',
      lastAuditDate:     asset.lastAuditDate ? asset.lastAuditDate.split('T')[0] : '',
      departmentId:      asset.departmentId ? String(asset.departmentId) : '',
      ownerId:           asset.ownerId ? String(asset.ownerId) : '',
      assignedUserId:    asset.assignedUserId ? String(asset.assignedUserId) : '',
      siteId:            asset.siteId ? String(asset.siteId) : '',
      equipmentAssignmentId: asset.equipmentAssignmentId ? String(asset.equipmentAssignmentId) : '',
    });
    setFormError('');
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    setFormError('');
    try {
      const url    = editingId ? `${API_BASE}/admin/assets/${editingId}` : `${API_BASE}/admin/assets`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) { setFormError(d.message || 'Error saving asset'); return; }
      showToast(editingId ? 'Asset updated' : 'Asset created');
      setShowForm(false);
      fetchAssets();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setFormSaving(false);
    }
  };

  const fld = (key) => ({
    value: form[key],
    onChange: (e) => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  // ── Render cell value ─────────────────────────────────────────────────────────
  const renderCell = (col, asset) => {
    switch (col) {
      case 'id':              return <span style={{ color: '#6c757d', fontSize: 12 }}>#{asset.id}</span>;
      case 'name':            return <span style={{ fontWeight: 600 }}>{asset.name}</span>;
      case 'assetType':       return asset.assetType || '—';
      case 'classification':  return asset.classification || '—';
      case 'status':
        return <span style={{ background: statusColor[asset.status] || '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{asset.status || '—'}</span>;
      case 'criticality':
        return asset.criticality
          ? <span style={{ background: critColor[asset.criticality] || '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{asset.criticality}</span>
          : '—';
      case 'serialNumber':    return <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{asset.serialNumber || '—'}</span>;
      case 'manufacturer':    return asset.manufacturer || '—';
      case 'model':           return asset.model || '—';
      case 'department':      return asset.department?.name || '—';
      case 'assignedUser':    return asset.assignedUser ? `${asset.assignedUser.firstName} ${asset.assignedUser.lastName}` : '—';
      case 'owner':           return asset.owner ? `${asset.owner.firstName} ${asset.owner.lastName}` : '—';
      case 'site':            return asset.site?.name || '—';
      case 'building':        return asset.building || '—';
      case 'floor':           return asset.floor || '—';
      case 'room':            return asset.room || '—';
      case 'rack':            return asset.rack || '—';
      case 'invoiceNumber':   return asset.invoiceNumber || '—';
      case 'budgetNumber':    return asset.budgetNumber || '—';
      case 'purchaseDate':    return fmtDate(asset.purchaseDate);
      case 'warrantyExpiration': {
        if (!asset.warrantyExpiration) return '—';
        const expired = new Date(asset.warrantyExpiration) < new Date();
        return <span style={{ color: expired ? '#dc3545' : 'inherit', fontWeight: expired ? 700 : 400 }}>{fmtDate(asset.warrantyExpiration)}{expired ? ' ⚠' : ''}</span>;
      }
      case 'endOfLifeDate':   return fmtDate(asset.endOfLifeDate);
      case 'depreciationValue': return asset.depreciationValue != null ? `€${Number(asset.depreciationValue).toLocaleString()}` : '—';
      case 'supplier':        return asset.supplier || '—';
      case 'lastAuditDate':   return fmtDate(asset.lastAuditDate);
      case 'notes':           return <span style={{ fontSize: 12, color: '#555', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{asset.notes || '—'}</span>;
      case 'createdAt':       return fmtDate(asset.createdAt);
      case 'actions':
        return binView ? (
          <span style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => handleRestore(asset.id)} style={s.btnGreen}>Restore</button>
            <button onClick={() => handleHardDelete(asset.id)} style={s.btnDanger}>Delete</button>
          </span>
        ) : (
          <span style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => openEdit(asset)} style={s.btnBlue}>Edit</button>
            <button onClick={() => openHistory(asset)} style={s.btnGray}>History</button>
            <button onClick={() => handleSoftDelete(asset.id)} style={s.btnDanger}>Delete</button>
          </span>
        );
      default: return '—';
    }
  };

  const visibleColDefs = ALL_COLUMNS.filter(c => visibleCols.has(c.key));
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // ── Styles ────────────────────────────────────────────────────────────────────
  const s = {
    btnBlue:   { padding: '4px 10px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    btnGreen:  { padding: '4px 10px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    btnGray:   { padding: '4px 10px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    btnDanger: { padding: '4px 10px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    input:     { padding: '6px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, minWidth: 0 },
    select:    { padding: '6px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, background: '#fff' },
    label:     { fontSize: 12, fontWeight: 600, color: '#495057', marginBottom: 2, display: 'block' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  };

  return (
    <div style={{ padding: '24px 32px', minHeight: '100vh', background: '#f8f9fa', boxSizing: 'border-box' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toast.type === 'error' ? '#dc3545' : '#28a745', color: '#fff', padding: '12px 20px', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/admin')} className="btn-back-nav">← Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#212529' }}>
            {binView ? '🗑 Recycle Bin' : '📦 Asset Management'}
          </h1>
          <p style={{ margin: '2px 0 0', color: '#6c757d', fontSize: 13 }}>
            {binView ? 'Deleted assets — restore or permanently remove' : `${total.toLocaleString()} total assets`}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search name, serial, manufacturer..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ ...s.input, width: 260 }}
        />
        <input
          placeholder="Filter by type..."
          value={filterType}
          onChange={e => { setFilterType(e.target.value); setPage(1); }}
          style={{ ...s.input, width: 160 }}
        />
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={s.select}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filterCrit} onChange={e => { setFilterCrit(e.target.value); setPage(1); }} style={s.select}>
          <option value="">All Criticality</option>
          {CRIT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(1); }} style={s.select}>
          <option value="">All Departments</option>
          {meta.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        <div style={{ flex: 1 }} />

        {/* Column visibility picker */}
        <div ref={colPickerRef} style={{ position: 'relative' }}>
          <button onClick={() => setShowColPicker(v => !v)} style={{ ...s.btnGray, padding: '7px 14px', fontSize: 13 }}>
            ⚙ Columns
          </button>
          {showColPicker && (
            <div style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 16, zIndex: 200, width: 220, boxShadow: '0 6px 20px rgba(0,0,0,.12)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#212529' }}>Visible Columns</div>
              {ALL_COLUMNS.map(col => (
                <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '3px 0' }}>
                  <input
                    type="checkbox"
                    checked={visibleCols.has(col.key)}
                    disabled={col.key === 'actions'}
                    onChange={e => {
                      setVisibleCols(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(col.key); else next.delete(col.key);
                        localStorage.setItem('assetMgmt_visibleCols', JSON.stringify([...next]));
                        return next;
                      });
                    }}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {!binView && (
          <>
            <button onClick={() => setShowBulkUpload(true)} style={{ ...s.btnGray, padding: '7px 14px', fontSize: 13 }}>⬆ Bulk Upload</button>
            <button onClick={handleExportCSV} style={{ ...s.btnGreen, padding: '7px 14px', fontSize: 13 }}>⬇ Export CSV</button>
            <button onClick={openCreate} style={{ ...s.btnBlue, padding: '7px 14px', fontSize: 13 }}>+ Add Asset</button>
          </>
        )}

        <button
          onClick={() => { setBinView(v => !v); setPage(1); }}
          style={{ padding: '7px 14px', background: binView ? '#007bff' : '#f8f9fa', color: binView ? '#fff' : '#495057', border: '1px solid #ced4da', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          {binView ? '← Active Assets' : '🗑 Recycle Bin'}
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 8, border: '1px solid #dee2e6', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: 13, width: '100%' }}>
          <thead>
            <tr style={{ background: '#343a40', color: '#fff' }}>
              {visibleColDefs.map(col => (
                <th key={col.key} style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 700, fontSize: 12, letterSpacing: '.4px' }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleColDefs.length} style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>Loading...</td></tr>
            ) : assets.length === 0 ? (
              <tr><td colSpan={visibleColDefs.length} style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
                {binView ? 'Recycle bin is empty.' : 'No assets found. Add one or adjust your filters.'}
              </td></tr>
            ) : assets.map((asset, idx) => (
              <tr key={asset.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #f0f0f0' }}
                onMouseEnter={e => e.currentTarget.style.background = '#eef4ff'}
                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f9f9f9'}
              >
                {visibleColDefs.map(col => (
                  <td key={col.key} style={{ padding: '8px 12px', verticalAlign: 'middle', whiteSpace: col.key === 'notes' ? 'normal' : 'nowrap' }}>
                    {renderCell(col.key, asset)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!binView && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
          <span style={{ color: '#6c757d', fontSize: 13 }}>
            Page {page} of {totalPages} — {total.toLocaleString()} assets
          </span>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ ...s.btnGray, opacity: page === 1 ? .4 : 1 }}>‹ Prev</button>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ ...s.btnGray, opacity: page >= totalPages ? .4 : 1 }}>Next ›</button>
        </div>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '30px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 860, padding: 32, boxShadow: '0 10px 40px rgba(0,0,0,.2)', position: 'relative' }}>
            <button onClick={() => setShowForm(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6c757d' }}>✕</button>
            <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700 }}>{editingId ? 'Edit Asset' : 'New Asset'}</h2>
            {formError && <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#721c24', fontSize: 13 }}>{formError}</div>}
            <form onSubmit={handleFormSubmit}>

              {/* Section: Basic Info */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#495057', borderBottom: '2px solid #007bff', paddingBottom: 4, marginBottom: 12 }}>Basic Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  <div style={s.formGroup}><label style={s.label}>Name *</label><input {...fld('name')} required style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Asset Type *</label><input {...fld('assetType')} required placeholder="Server, Laptop, Monitor..." style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Classification *</label>
                    <select {...fld('classification')} required style={s.select}>
                      <option value="">Select...</option>
                      {['Public','Internal','Confidential','Secret'].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Status</label>
                    <select {...fld('status')} style={s.select}>
                      {STATUS_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Criticality</label>
                    <select {...fld('criticality')} style={s.select}>
                      <option value="">Select...</option>
                      {CRIT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Serial Number</label><input {...fld('serialNumber')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Manufacturer</label><input {...fld('manufacturer')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Model</label><input {...fld('model')} style={s.input} /></div>
                  <div style={{ ...s.formGroup, gridColumn: 'span 2' }}><label style={s.label}>Description</label><textarea {...fld('description')} rows={2} style={{ ...s.input, resize: 'vertical' }} /></div>
                </div>
              </div>

              {/* Section: Assignment */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#495057', borderBottom: '2px solid #28a745', paddingBottom: 4, marginBottom: 12 }}>Assignment</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  <div style={s.formGroup}><label style={s.label}>Department *</label>
                    <select {...fld('departmentId')} required style={s.select}>
                      <option value="">Select...</option>
                      {meta.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Owner</label>
                    <select {...fld('ownerId')} style={s.select}>
                      <option value="">Select...</option>
                      {meta.users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Assigned To</label>
                    <select {...fld('assignedUserId')} style={s.select}>
                      <option value="">Unassigned</option>
                      {meta.users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Site / Client</label>
                    <select {...fld('siteId')} style={s.select}>
                      <option value="">None</option>
                      {meta.sites.map(s => <option key={s.id} value={s.id}>{s.name}{s.country ? ` (${s.country.name})` : ''}</option>)}
                    </select>
                  </div>
                  <div style={s.formGroup}><label style={s.label}>Equipment Assign. ID</label><input {...fld('equipmentAssignmentId')} type="number" style={s.input} /></div>
                </div>
              </div>

              {/* Section: Location */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#495057', borderBottom: '2px solid #ffc107', paddingBottom: 4, marginBottom: 12 }}>Location</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                  <div style={s.formGroup}><label style={s.label}>Building</label><input {...fld('building')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Floor</label><input {...fld('floor')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Room</label><input {...fld('room')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Rack</label><input {...fld('rack')} style={s.input} /></div>
                </div>
              </div>

              {/* Section: Financial */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#495057', borderBottom: '2px solid #dc3545', paddingBottom: 4, marginBottom: 12 }}>Financial & Lifecycle</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                  <div style={s.formGroup}><label style={s.label}>Purchase Date</label><input {...fld('purchaseDate')} type="date" style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Supplier</label><input {...fld('supplier')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Invoice #</label><input {...fld('invoiceNumber')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Budget #</label><input {...fld('budgetNumber')} style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Warranty Expiry</label><input {...fld('warrantyExpiration')} type="date" style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>End of Life</label><input {...fld('endOfLifeDate')} type="date" style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Depreciation (€)</label><input {...fld('depreciationValue')} type="number" step="0.01" style={s.input} /></div>
                  <div style={s.formGroup}><label style={s.label}>Last Audit Date</label><input {...fld('lastAuditDate')} type="date" style={s.input} /></div>
                </div>
              </div>

              {/* Section: Notes */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#495057', borderBottom: '2px solid #6c757d', paddingBottom: 4, marginBottom: 12 }}>Notes & Config</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={s.formGroup}><label style={s.label}>Config Specs</label><textarea {...fld('configSpecs')} rows={3} style={{ ...s.input, resize: 'vertical' }} /></div>
                  <div style={s.formGroup}><label style={s.label}>Notes</label><textarea {...fld('notes')} rows={3} style={{ ...s.input, resize: 'vertical' }} /></div>
                  <div style={s.formGroup}><label style={s.label}>Related Tickets</label><input {...fld('relatedTickets')} style={s.input} /></div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ ...s.btnGray, padding: '10px 20px', fontSize: 14 }}>Cancel</button>
                <button type="submit" disabled={formSaving} style={{ ...s.btnBlue, padding: '10px 24px', fontSize: 14, opacity: formSaving ? .6 : 1 }}>
                  {formSaving ? 'Saving...' : (editingId ? 'Update Asset' : 'Create Asset')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── History Modal ───────────────────────────────────────────────────── */}
      {historyAsset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,.2)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Asset History</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6c757d' }}>{historyAsset.name} (#{historyAsset.id})</p>
              </div>
              <button onClick={() => setHistoryAsset(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6c757d' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
              {historyLoading ? <p style={{ color: '#6c757d', textAlign: 'center' }}>Loading...</p>
                : history.length === 0 ? <p style={{ color: '#6c757d', textAlign: 'center' }}>No history found.</p>
                : history.map((h, i) => (
                <div key={h.id} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: h.action === 'CREATE' ? '#28a745' : h.action === 'DELETE' ? '#dc3545' : h.action === 'RESTORE' ? '#17a2b8' : '#007bff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700 }}>
                    {h.action[0]}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
                      {h.action}{h.field ? ` — ${h.field}` : ''}
                    </div>
                    {(h.oldValue || h.newValue) && (
                      <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                        {h.oldValue && <span style={{ textDecoration: 'line-through', marginRight: 8 }}>{h.oldValue}</span>}
                        {h.newValue && <span style={{ color: '#007bff' }}>→ {h.newValue}</span>}
                      </div>
                    )}
                    {h.notes && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{h.notes}</div>}
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                      {h.changedBy ? `${h.changedBy.firstName} ${h.changedBy.lastName}` : 'System'} · {fmtDate(h.changedAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Upload Modal ────────────────────────────────────────────────── */}
      {showBulkUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, padding: 32, boxShadow: '0 10px 40px rgba(0,0,0,.2)', position: 'relative' }}>
            <button onClick={() => { setShowBulkUpload(false); setBulkResult(null); setBulkFile(null); }} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6c757d' }}>✕</button>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Bulk Upload Assets from CSV</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6c757d' }}>Upload a CSV file. Required columns: <code>name</code>, <code>assetType</code>, <code>classification</code>, and <code>departmentId</code> or <code>departmentName</code>.</p>

            <button onClick={handleDownloadTemplate} style={{ ...s.btnGray, marginBottom: 20, padding: '8px 16px' }}>⬇ Download CSV Template</button>

            <div style={{ border: '2px dashed #ced4da', borderRadius: 8, padding: 24, textAlign: 'center', marginBottom: 16, background: '#f8f9fa' }}>
              <input type="file" accept=".csv" id="csv-upload" style={{ display: 'none' }} onChange={e => setBulkFile(e.target.files[0])} />
              <label htmlFor="csv-upload" style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{bulkFile ? bulkFile.name : 'Click to select CSV'}</div>
                <div style={{ fontSize: 12, color: '#6c757d' }}>Max 5 MB</div>
              </label>
            </div>

            {bulkResult && (
              <div style={{ background: bulkResult.error ? '#f8d7da' : '#d4edda', border: `1px solid ${bulkResult.error ? '#f5c6cb' : '#c3e6cb'}`, borderRadius: 6, padding: 14, marginBottom: 16, fontSize: 13 }}>
                {bulkResult.error ? (
                  <span style={{ color: '#721c24' }}>Error: {bulkResult.error}</span>
                ) : (
                  <>
                    <div style={{ color: '#155724', fontWeight: 700 }}>✓ {bulkResult.created} asset{bulkResult.created !== 1 ? 's' : ''} created</div>
                    {bulkResult.errors?.length > 0 && (
                      <div style={{ marginTop: 8, color: '#721c24' }}>
                        <div style={{ fontWeight: 700 }}>{bulkResult.errors.length} error{bulkResult.errors.length !== 1 ? 's' : ''}:</div>
                        {bulkResult.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowBulkUpload(false); setBulkResult(null); setBulkFile(null); }} style={{ ...s.btnGray, padding: '9px 18px' }}>Close</button>
              <button onClick={handleBulkUpload} disabled={!bulkFile || bulkLoading} style={{ ...s.btnBlue, padding: '9px 20px', opacity: !bulkFile || bulkLoading ? .5 : 1 }}>
                {bulkLoading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
